import React, { ComponentProps, Fragment, ReactNode } from 'react';
import { cn } from '@v2/shared/lib/utils';
import Container from '@v2/shared/components/Container';
import { Category } from '@v2/shared/lib/types';
import { Disclosure, Transition } from '@headlessui/react';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Logo from './Navigation/Logo';
import Search from './Navigation/Search';
import CartButton from './Navigation/CartButton';
import AccountLinks from './Navigation/AccountLinks';
import CategoryMenu from './Navigation/CategoryMenu';
import MobileCategories from './Navigation/MobileCategories';

type Props = {
  categories?: Category[];
  /** The brand's mark. Every brand's differs in file and in the size it draws it at. */
  logo: ComponentProps<typeof Logo>;
  /** The search input's id, which its listbox is derived from — see Search. */
  searchId: string;
  /**
   * Links carried beside the account ones from md up, and above them in the panel the
   * hamburger opens. MagZoom's About/FAQ/Contact; MagSpot has none, and passing none is
   * what makes the account links show from sm rather than md.
   */
  extraLinks?: ReactNode;
};

/*
 * The header runs to the edges below md.
 *
 * Tailwind derives `container`'s max-widths from `screens`, and xs (375px) and sm (414px)
 * are phone widths -- so between 414px and 768px the container stops growing and the
 * header sits in a 414px column with the page showing down either side. That is fine for
 * a content column on a wide screen and wrong for a header, which should reach the edges
 * until there is a reason not to.
 *
 * max-w-none undoes it, and the breakpoint variants put back exactly what `container`
 * would have set from md up -- they have to be restated because max-w-none is
 * unconditional and would otherwise win at every width. Utilities are emitted after the
 * container's own rules and in breakpoint order, so each one wins where it applies.
 */
const HEADER_WIDTH = 'max-w-none md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl';

/**
 * The header MagSpot and MagZoom share, ported from their
 * `domain-overrides/<site>/layouts/full/header.html`.
 *
 * Two strips, as there: a white one carrying the logo, the account links and the bag,
 * and a primary-coloured one carrying the category dropdown and the search box. In
 * normal flow, so nothing measures a height.
 *
 * `bg-primary` is themed — it resolves to `--v2-primary`, which styles/v2.css sets per
 * brand under `[data-brand=...]`. The class is the same one every brand renders; only
 * the value differs, which is why the strip needs no prop.
 *
 * The legacy headers hide the category list behind a drawer on a phone
 * (`js-module="snapper:toggle"`, a jQuery plugin the V2 tree has no counterpart for);
 * this collapses it behind a Disclosure instead.
 *
 * TopMags deliberately does not use this. Its header is a different arrangement — the
 * mark centred between the hamburger and the bag, account items stacked, the coloured
 * strip only from md — and reconciling the two would mean a prop for nearly every line.
 * These two are the pair that were the same markup twice.
 */
export default function TwoStripNavigation({ categories, logo, searchId, extraLinks }: Props) {
  return (
    <nav>
      <Disclosure>
        {/* px-2.5 below md. The Container's default px-5 spent 20px of a 393px screen on
            each gutter, which is affordable on a desktop and not on a phone. Both rows
            take the same value so the bag and the search bar below it stay on one line
            down each edge; md keeps px-5.

            pt-4 for the same reason TopMags has it: py-2 left 8px above the logo against
            8px and a rule below it, which reads as top-heavy once the strip behind it is
            white. md:py-3 wins back at md, being the later rule. */}
        <Container className={cn(HEADER_WIDTH, 'bg-white px-2.5 py-2 pt-4 md:px-5 md:py-3')}>
          <div className="flex items-center justify-between gap-3">
            <Disclosure.Button className="text-slate-500 md:hidden px-1" aria-label="Menu">
              <FontAwesomeIcon icon={faBars} size="lg" />
            </Disclosure.Button>
            <Logo {...logo} />
            <div className="flex items-center gap-4">
              {/* With extra links there are two arrangements rather than one: from md the
                  account links follow them after the dash the legacy template draws, and
                  between sm and md they stand alone. Without them there is nothing to
                  make room for, so the single span starts at sm. */}
              {extraLinks && (
                <span className="hidden md:inline-flex md:items-center">
                  {extraLinks}
                  <span className="px-2 text-slate-400">&ndash;</span>
                  <AccountLinks />
                </span>
              )}
              <span className={cn('hidden sm:inline-flex', extraLinks && 'md:hidden')}>
                <AccountLinks />
              </span>
              <CartButton />
            </div>
          </div>
        </Container>

        {/* The brand colour starts at md, as TopMags' does. Below it the strip holds
            nothing but the search box -- CategoryMenu is hidden md:block -- so the colour
            was a band of brand behind one white input rather than a bar of navigation,
            and it cost the search bar its contrast against the page.

            The rule replaces it: without the colour the header would end in nothing and
            run into the page. Above md the colour does that job and the border is off. */}
        <div className="border-b border-slate-200 md:border-0 md:bg-primary">
          <Container className={cn(HEADER_WIDTH, 'px-2.5 md:px-5')}>
            {/* `relative` is load-bearing: the category dropdown's panel is absolutely
                positioned with `top-full`, and without a positioned ancestor it resolves
                against the page and opens at the bottom of it.

                The bar's padding is on this row rather than on the Container for the same
                reason — it makes the row's bottom edge the bar's bottom edge, which is
                what the panel hangs off. On the Container the panel would open 8px up
                inside the colour. */}
            <div className="relative flex flex-col items-stretch gap-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
              <div className="hidden md:block">
                <CategoryMenu categories={categories} />
              </div>
              <div className="w-full md:w-2/3">
                <Search id={searchId} />
              </div>
            </div>
          </Container>

          <Transition
            as={Fragment}
            enter="transition duration-200 ease-out"
            enterFrom="transform -translate-y-2 opacity-0"
            enterTo="transform translate-y-0 opacity-100"
            leave="transition duration-150 ease-out"
            leaveFrom="transform translate-y-0 opacity-100"
            leaveTo="transform -translate-y-2 opacity-0"
          >
            <Disclosure.Panel className="md:hidden bg-white border-0 border-b border-solid border-slate-200">
              <Container className={cn(HEADER_WIDTH, 'py-3')}>
                {extraLinks && <div className="pb-3 text-center">{extraLinks}</div>}
                {/* sm:hidden, unlike the links above it: from sm the account links are
                    already in the header, and only the categories are left to open. */}
                <div className="sm:hidden pb-3 text-center">
                  <AccountLinks />
                </div>
                <MobileCategories categories={categories} />
              </Container>
            </Disclosure.Panel>
          </Transition>
        </div>
      </Disclosure>
    </nav>
  );
}
